import { useState } from 'react';
import { useSelector } from 'react-redux';
import AppUsageFilter from './components/filter';
import CompanionAppUsageReportHeader from './components/appUsageReportHeader';
import YearOverYearAlert from './components/yearOverYearAlert';
import { useGetCompanionAppUsageReportMutation } from '../../../../features/authentication/companionAppUsageApiSlice';
import { useGetCompanionAppUsageFiltersMutation } from '../../../../features/authentication/persistApiSlice';
import CustomToast from '../../../../components/Toast';
import DataSummaryMetrics from './components/dataSummaryMetrics';
import ActiveUserTrends from './components/activeUserTrendsChart';
import FeatureUsageChart from './components/featureUsageChart';
import RetentionRateCard from './components/retentionRateCard';
import DataSummaryTable from './components/dataSummaryTable';
import TopUsageRanking from './components/topUsageRanking';
import CompanionAppUsageDateRange from '../../../../enums/companionAppUsageDateRange';
import usePrintPdfGenerator from '../../../../utils/PrintPdfGenerator';
import { DateFormatter } from '../../../../utils/DateFormatter';
import { exportCsvReport } from './utils/exportCsv';
import { printPdfReport } from './utils/printPdf';

const CompanionAppUsageReport = () => {
  const [showReport, setShowReport] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isCsvExporting, setIsCsvExporting] = useState(false);
  const [reportRunDate, setReportRunDate] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState(null);
  const [yearOverYearComparison, setYearOverYearComparison] = useState(false);

  const [getCompanionAppUsageReport] = useGetCompanionAppUsageReportMutation();
  const [getCompanionAppUsageFilters] = useGetCompanionAppUsageFiltersMutation();
  const { printPDF, isPdfGenerating } = usePrintPdfGenerator();

  const ferpaEnabled = useSelector((state) => state.auth?.ferpaEnabled ?? false);

  const fetchReport = async () => {
    try {
      setIsLoading(true);
      setHasError(false);

      const filters = await getCompanionAppUsageFilters().unwrap();

      setSelectedFilters({
        districtName: filters?.districtName || 'All Districts',
        schoolName: filters?.schoolName || 'All Schools',
        teacherName: filters?.teacherName || 'All Teachers',
        studentName: filters?.studentName || 'All Students',
        dateRangeName: getDateRangeName(filters?.dateRange),
        startDateUtc: filters?.startDateUtc,
        endDateUtc: filters?.endDateUtc,
        isCustomRange: filters?.dateRange === CompanionAppUsageDateRange.CustomRange
      });

      setYearOverYearComparison(filters?.includeYearOverYearComparison ?? true);

      const requestParams = {
        districtId: filters?.districtId ?? -1,
        schoolId: filters?.schoolId ?? -1,
        teacherId: filters?.teacherId ?? -1,
        studentId: filters?.studentId ?? -1,
        dateRange: filters?.dateRange ?? CompanionAppUsageDateRange.FromStartOfCurrentSchoolYear,
        includeYearOverYearComparison: filters?.includeYearOverYearComparison ?? true
      };

      if (filters?.dateRange === CompanionAppUsageDateRange.CustomRange) {
        if (filters?.startDateUtc) {
          requestParams.startDateUtc = filters.startDateUtc;
        }
        if (filters?.endDateUtc) {
          requestParams.endDateUtc = filters.endDateUtc;
        }
      }

      const response = await getCompanionAppUsageReport(requestParams).unwrap();

      setReportRunDate(response?.reportRunUtc ? DateFormatter.toLongDateTime2(response.reportRunUtc) : null);

      if (response) {
        setReportData(response);
      } else {
        setReportData(null);
      }
    } catch (error) {
      setHasError(true);
      const message = error?.data?.error || error?.data?.message || 'Failed to load the report';
      CustomToast(message, 'error');
      setReportData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const getDateRangeName = (dateRangeId) => {
    const dateRangeMap = {
      [CompanionAppUsageDateRange.FromStartOfCurrentSchoolYear]: 'From Start of Current School Year',
      [CompanionAppUsageDateRange.Last7Days]: 'Last 7 Days',
      [CompanionAppUsageDateRange.Last30Days]: 'Last 30 Days',
      [CompanionAppUsageDateRange.Last90Days]: 'Last 90 Days',
      [CompanionAppUsageDateRange.CustomRange]: 'Custom Date Range'
    };
    return dateRangeMap[dateRangeId] || 'N/A';
  };

  const handleCreateReport = async () => {
    setShowReport(true);
    await fetchReport();
  };

  const handleGoBack = () => {
    setShowReport(false);
    setReportData(null);
    setHasError(false);
  };

  const handleExportToCSV = async () => {
    try {
      setIsCsvExporting(true);
      exportCsvReport({
        reportData,
        selectedFilters,
        yearOverYearComparison,
        ferpaEnabled
      });
    } catch (error) {
      CustomToast(error?.message || 'Failed to export report', 'error');
    } finally {
      setIsCsvExporting(false);
    }
  };

  const handlePrintToPDF = async () => {
    await printPdfReport({ reportData, printPDF, ferpaEnabled });
  };

  return (
    <>
      {!showReport && (
        <AppUsageFilter handleCreateReport={handleCreateReport} />
      )}

      {showReport && (
        <div id="reportWrapper">
          <div className="white-card mb-4">
            <CompanionAppUsageReportHeader
              reportTitle="Companion App Usage Report"
              filters={selectedFilters ? { ...selectedFilters, studentName: selectedFilters.studentName === 'All Students' || ferpaEnabled ? selectedFilters.studentName : 'name hidden' } : selectedFilters}
              reportRunDate={reportRunDate}
              exportHandler={handleExportToCSV}
              isExporting={isCsvExporting}
              printHandler={handlePrintToPDF}
              isPdfGenerating={isPdfGenerating}
              onBackClick={handleGoBack}
              isLoading={isLoading}
              yearOverYearComparison={yearOverYearComparison}
            />

            {!hasError && (
              <>
                {yearOverYearComparison &&
                  <YearOverYearAlert
                    hasDeclineDetected={reportData?.dataSummary?.hasDeclineDetected ?? false}
                    isLoading={isLoading}
                  />
                }

                <DataSummaryMetrics
                  dataSummary={reportData?.dataSummary}
                  isLoading={isLoading}
                  yearOverYearComparison={yearOverYearComparison}
                />
                
                <ActiveUserTrends
                  activeUserTrends={reportData?.activeUsersTrend}
                  isLoading={isLoading}
                  yearOverYearComparison={yearOverYearComparison}
                />

                <FeatureUsageChart
                  features={reportData?.featureUsage?.features}
                  isLoading={isLoading}
                />

                <div className="row g-3 pt-3">
                  <div className="col-12 col-xl-6">
                    <RetentionRateCard
                      retentionRate={reportData?.retentionRate}
                      isLoading={isLoading}
                      yearOverYearComparison={yearOverYearComparison}
                    />
                  </div>
                  <div className="col-12 col-xl-6">
                    <DataSummaryTable
                      dataSummary={reportData?.dataSummary}
                      isLoading={isLoading}
                      yearOverYearComparison={yearOverYearComparison}
                    />
                  </div>
                </div>

                <TopUsageRanking
                  rankings={reportData?.topUsageRanking ?? reportData?.topUsageRankings ?? {
                    districts: reportData?.topDistricts ?? reportData?.districtRankings ?? [],
                    schools: reportData?.topSchools ?? reportData?.schoolRankings ?? [],
                    students: reportData?.topStudents ?? reportData?.studentRankings ?? []
                  }}
                  isLoading={isLoading}
                  ferpaEnabled={ferpaEnabled}
                />
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CompanionAppUsageReport;